/**
 * Where the audio actually goes.
 *
 * The claim this page makes is a negative one — your audio does not pass
 * through us — and a negative is hard to assert in prose without sounding
 * defensive. The shape says it instead:
 *
 *   - the provider sits ABOVE the line with a pair of arrows, because that is a
 *     round trip and not a stop on the way. Drawn as a station in the middle,
 *     it would suggest the audio flows onward through it;
 *   - Kizuna AI hangs BELOW on a dashed branch that is not on the path at all.
 *     What travels there is named on the branch: a key and a usage count.
 *
 * The provider node shows two glyphs because on-device is one of the two things
 * it can be. Drawing a cloud there would contradict the rest of the site.
 */

import { Logo } from '@/components/ui/Logo';
import { Arrow } from './Arrow';
import './RoutingDiagram.scss';

interface RoutingDiagramProps {
  you: string;
  sokuji: string;
  provider: string;
  providerNote: string;
  up: string;
  down: string;
  youSpeak: string;
  theyHear: string;
  mic: string;
  app: string;
  them: string;
  kizuna: string;
  kizunaCarries: string;
  /** The word between the two provider glyphs. */
  or: string;
  label: string;
}

const ICON = {
  person: (
    <>
      <circle cx="12" cy="8.4" r="3.4" />
      <path d="M5.6 19.6c0-3.5 2.9-6.4 6.4-6.4s6.4 2.9 6.4 6.4" />
    </>
  ),
  cloud: <path d="M7.4 18.6a4.4 4.4 0 0 1 .7-8.75 6 6 0 0 1 11.3 1.7 3.6 3.6 0 0 1-.6 7.05z" />,
  chip: (
    <>
      <rect x="6.5" y="6.5" width="11" height="11" rx="2.2" />
      <path d="M9.6 3.2v3.3M14.4 3.2v3.3M9.6 17.5v3.3M14.4 17.5v3.3M3.2 9.6h3.3M3.2 14.4h3.3M17.5 9.6h3.3M17.5 14.4h3.3" />
    </>
  ),
  sokuji: (
    <>
      <rect x="4" y="4" width="16" height="16" rx="4" />
      <path d="M9 15V9l3 3 3-3v6" />
    </>
  ),
  mic: (
    <>
      <rect x="9.4" y="2.8" width="5.2" height="10" rx="2.6" />
      <path d="M6.4 11.4a5.6 5.6 0 0 0 11.2 0M12 17v3.4" />
    </>
  ),
  app: (
    <>
      <rect x="2.8" y="4.6" width="18.4" height="14.8" rx="2.6" />
      <path d="M2.8 9h18.4" />
    </>
  ),
};

function Glyph({ children }: { children: React.ReactNode }) {
  return (
    <svg
      width="19"
      height="19"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

export function RoutingDiagram(props: RoutingDiagramProps) {
  return (
    <div className="routing" role="img" aria-label={props.label}>
      {/* Row 1: the provider, and the round trip to it. Placed above the line
          rather than on it, because it is a there-and-back and not a stop. */}
      <div className="routing__above">
        <div className="routing__stop">
          <span className="routing__node routing__node--wide">
            <Glyph>{ICON.cloud}</Glyph>
            <span className="routing__or site-label">{props.or}</span>
            <Glyph>{ICON.chip}</Glyph>
          </span>
          <span className="routing__caption site-label">
            {props.provider}
            <br />
            <span className="routing__caption-sub site-label">{props.providerNote}</span>
          </span>
        </div>

        <div className="routing__trip">
          <span className="routing__trip-label routing__trip-label--up site-label">{props.up}</span>
          <svg
            className="routing__trip-arrows"
            width="34"
            height="40"
            viewBox="0 0 34 40"
            aria-hidden="true"
          >
            <path d="M9 39 L9 5" stroke="var(--site-ink-faint)" strokeWidth="1.5" />
            <path d="M5 11 L9 4 L13 11 Z" fill="var(--site-ink-faint)" />
            <path d="M25 1 L25 35" stroke="var(--color-accent)" strokeWidth="1.5" />
            <path d="M21 29 L25 36 L29 29 Z" fill="var(--color-accent)" />
          </svg>
          <span className="routing__trip-label routing__trip-label--down site-label">
            {props.down}
          </span>
        </div>
      </div>

      {/* Row 2: the path itself. */}
      <div className="routing__stop routing__col-you">
        <span className="routing__node"><Glyph>{ICON.person}</Glyph></span>
        <span className="routing__caption site-label">{props.you}</span>
      </div>
      <span className="routing__hop routing__col-a1">
        <span className="routing__hop-label site-label">{props.youSpeak}</span>
        <Arrow className="routing__arrow" width={64} />
      </span>
      <div className="routing__stop routing__col-sokuji">
        <span className="routing__node routing__node--ours">
          <Logo size={19} />
        </span>
        <span className="routing__caption site-label">{props.sokuji}</span>
      </div>
      <span className="routing__hop routing__col-a2">
        <span className="routing__hop-label site-label" />
        <Arrow className="routing__arrow" width={64} />
      </span>
      <div className="routing__stop routing__col-mic">
        <span className="routing__node routing__node--ours"><Glyph>{ICON.mic}</Glyph></span>
        <span className="routing__caption site-label">{props.mic}</span>
      </div>
      <span className="routing__hop routing__col-a3">
        <span className="routing__hop-label site-label" />
        <Arrow className="routing__arrow" width={64} />
      </span>
      <div className="routing__stop routing__col-app">
        <span className="routing__node"><Glyph>{ICON.app}</Glyph></span>
        <span className="routing__caption site-label">{props.app}</span>
      </div>
      <span className="routing__hop routing__col-a4">
        <span className="routing__hop-label site-label">{props.theyHear}</span>
        <Arrow className="routing__arrow" width={64} />
      </span>
      <div className="routing__stop routing__col-them">
        <span className="routing__node"><Glyph>{ICON.person}</Glyph></span>
        <span className="routing__caption site-label">{props.them}</span>
      </div>

      {/* Row 3: off the path entirely, and dashed, because nothing that
          matters here travels down it. */}
      <div className="routing__branch">
        <svg
          className="routing__branch-line"
          width="22"
          height="34"
          viewBox="0 0 22 34"
          aria-hidden="true"
        >
          <path
            d="M11 0 L11 28"
            stroke="var(--site-line-dashed)"
            strokeWidth="1.5"
            strokeDasharray="4 4"
          />
          <path d="M7 22 L11 29 L15 22 Z" fill="var(--site-line-dashed)" />
        </svg>
        {/* A stop like the others, so the box is as wide as its caption. */}
        <div className="routing__stop">
          <span className="routing__node routing__node--dashed">{props.kizuna}</span>
          <span className="routing__caption site-label">{props.kizunaCarries}</span>
        </div>
      </div>
    </div>
  );
}
